import React from 'react';

import { Button, Group, Modal, Stack, Text, Title } from '@mantine/core';
import useUndoCreditDispensation from 'hooks/creditDispensation/useUndoCreditDispensation';
import { CreditDispensation } from 'types';
import notificationHandler from 'utils/notifications';

interface IModalCreditDispensationUndo {
  opened: boolean;
  handleClose: () => void;
  creditDispensation: CreditDispensation | null;
}

const ModalCreditDispensationUndo = ({
  opened,
  creditDispensation,
  handleClose,
}: IModalCreditDispensationUndo): JSX.Element => {
  const undoMutation = useUndoCreditDispensation();
  const { successNotification, errorNotification } = notificationHandler();

  const handleUndo = (): void => {
    if (!creditDispensation) return;

    undoMutation.mutate(creditDispensation.creditDispensationId, {
      onSuccess: () => {
        successNotification(
          'Rollover undone',
          'Rollover credits were successfully moved back to the original dispensation.',
        );
        handleClose();
      },
      onError: error => {
        errorNotification(
          'Undo rollover failed',
          error.response?.data?.message ?? 'Something went wrong while undoing the rollover.',
        );
      },
    });
  };

  return (
    <Modal
      centered
      opened={opened}
      onClose={handleClose}
      title={<Title order={4}>Undo Rollover Credits</Title>}
      size='auto'
    >
      <Stack>
        <Text>Are you sure you want to undo this rollover credit dispensation?</Text>
        <Text color='dimmed'>
          This action will move any remaining credits back to the original credit dispensation.
        </Text>
        <Group position='right'>
          <Button
            onClick={handleUndo}
            color='blue'
            loading={undoMutation.isLoading}
            disabled={!creditDispensation || creditDispensation.creditsUsed > 0}
            data-cy='submitUndoCreditDispensationButton'
          >
            Undo rollover
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};

export default ModalCreditDispensationUndo;
